import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { Equals, IsEmail, IsNotEmpty, IsPhoneNumber } from 'class-validator';
import { action, makeAutoObservable, observable } from 'mobx';
import { NavigateFunction } from 'react-router-dom';

import { SignInWithSSOCollaboration } from '@/modules/recognition/application/collaborations/sso/signin-with-sso.collaboration';
import { SignUpWithSSOCollaboration } from '@/modules/recognition/application/collaborations/sso/signup-with-sso.collaboration';
import { SignUpWithSSODto } from '@/shared/application/interfaces/pegasus-io.gateway';
import { UrlUtils } from '@/shared/application/utils/url';
import {
  IsFullname,
  IsStrongPassword,
  Match,
  ObservableForm,
} from '@/shared/presentation/forms/BaseForm';
import { MicrofrontendViewModel } from '@/shared/presentation/Microfrontend.vm';

type SignInWithSSOViewModelProps = {
  navigate: NavigateFunction;
  provider: string;
};

class SignUpForm {
  @IsEmail({}, { message: 'E-mail inválido' })
  email = '';

  @IsFullname()
  name = '';

  @IsPhoneNumber('BR', { message: 'Telefone inválido' })
  phoneNumber = '';

  @IsNotEmpty({ message: 'Campo obrigatório' })
  @IsStrongPassword()
  password = '';

  @IsNotEmpty({ message: 'Campo obrigatório' })
  @Match((o) => o.password, { message: 'As senhas devem ser iguais' })
  passwordConfirmation = '';

  @Equals(true, { message: 'Você deve aceitar os termos de uso' })
  isTermsAccepted = false;
}

@ViewModel()
export class SignInWithSSOViewModel implements IReactiveModel {
  @observable form = new ObservableForm(SignUpForm);
  @observable isSubmitting = false;
  @observable isLoading = true;
  @observable hasSignInError = false;
  @observable signUpRequired = false;
  @observable hasSignUpError = false;

  private navigate: NavigateFunction;

  constructor(
    private signInWithSSOCollaboration: SignInWithSSOCollaboration,
    private signUpWithSSOCollaboration: SignUpWithSSOCollaboration,
    private microfrontendViewModel: MicrofrontendViewModel,
  ) {
    makeAutoObservable(this);
  }

  @action
  async onInit(props: SignInWithSSOViewModelProps) {
    try {
      const { navigate, provider } = props;
      this.navigate = navigate;

      if (!provider) {
        throw new Error('Parceiro não informado.');
      }

      const registration = UrlUtils.getQueryParam('registration');
      const token = UrlUtils.getQueryParam('token');

      if (!token || !registration) {
        throw new Error('Parâmetros inválidos.');
      }

      await this.signInWithSSOCollaboration.run({
        ssoRegistrationGuid: registration,
        providerToken: token,
      });

      await this.microfrontendViewModel.identifyCurrentAccount();

      this.navigate('/');
    } catch (error) {
      console.error(error);

      if (error.response?.data?.identifier === 'SignUpRequired') {
        this.signUpRequired = true;

        this.form.setValueAndValidateValue('email', error.response.data.data.externalUserEmail);
        this.form.setValueAndValidateValue('name', error.response.data.data.externalUserName);

        return;
      }

      this.hasSignInError = true;
    } finally {
      this.isLoading = false;
    }
  }

  async signUp() {
    try {
      const registration = UrlUtils.getQueryParam('registration');
      const token = UrlUtils.getQueryParam('token');

      if (this.form.hasError) {
        return;
      }

      this.isSubmitting = true;
      this.hasSignUpError = false;

      const dto: SignUpWithSSODto = {
        name: this.form.values.name?.trim(),
        phoneNumber: this.form.values.phoneNumber.replace(/\D/g, ''),
        password: this.form.values.password,
        passwordConfirmation: this.form.values.passwordConfirmation,
        email: this.form.values.email,
        ssoRegistrationGuid: registration,
        providerToken: token,
      };

      await this.signUpWithSSOCollaboration.run(dto);
      await this.microfrontendViewModel.identifyCurrentAccount();

      this.navigate('/');
    } catch (error) {
      console.error(error);
      this.hasSignUpError = true;
    } finally {
      this.isSubmitting = false;
    }
  }
}
